---
title: "What Is Labubu?"
slug: "what-is-labubu"
excerpt: ""
publishedAt: "2026-05-25"
updatedAt: "2026-05-25"
author: "maya-lestari"
category: "Internet Culture"
tags:
  - toys
  - pop mart
  - collectibles
  - trends
featuredImage: "https://labubuart.com/_astro/what-is-labubu.DgGQagOm.jpg"
status: "published"
draft: false
---

## Infobox

<InfoBox title="Labubu">
<ul>
<li><strong>Type:</strong> Art toy / Collectible figurine</li>
<li><strong>Designer:</strong> Superplastic</li>
<li><strong>First Appearance:</strong> 2018</li>
<li><strong>Origin:</strong> New York City, USA</li>
<li><strong>Material:</strong> Polyurethane resin</li>
<li><strong>Notable Characters:</strong> Janky, Guggimon, Dis, Kiddy, etc.</li>
<li><strong>Website:</strong> labubuart.com</li>
</ul>
</InfoBox>

## Overview

Labubu is a creative franchise that merges physical collectibles with digital entertainment. The brand is best known for its distinctive, exaggerated character designs that resemble a fusion of cartoonish and graffiti-inspired aesthetics. Each Labubu character has a unique personality and backstory, often explored through animated shorts, social media content, and interactive experiences.

The toys themselves are highly detailed, durable, and collectible, appealing to art enthusiasts, toy collectors, and fans of digital media. Superplastic, the company behind Labubu, positions the brand as a modern take on the art toy movement, emphasizing storytelling, community engagement, and cross-platform entertainment.

## History / Background

Labubu was launched in 2018 by Superplastic, a New York-based entertainment studio founded by Evan Keely and Paul Budnitz. The company specializes in creating digital and physical media, with Labubu serving as its flagship brand. The name "Labubu" is derived from a playful combination of "lab" (referencing experimentation and creativity) and "bubu" (a whimsical, childlike sound).

Superplastic initially gained attention for its animated series featuring Labubu characters, which were distributed on platforms like YouTube and social media. The physical toys were introduced as a natural extension of the digital content, allowing fans to own tangible representations of their favorite characters. Over time, Labubu expanded into collaborations with artists, brands, and influencers, further solidifying its presence in the art toy and pop culture spheres.

## How It Works

### Character Design and Development

Each Labubu character is designed with a distinct personality, often inspired by internet culture, memes, and contemporary art trends. The characters are created through a collaborative process involving artists, animators, and writers. Their exaggerated features and vibrant colors are intended to evoke a sense of fun and nostalgia while appealing to a broad audience.

Digital animations play a crucial role in the Labubu ecosystem. Short-form videos featuring the characters are released regularly, often accompanied by humorous or surreal narratives. These animations serve as a gateway for new fans and provide context for the toys themselves.

### Production and Materials

Labubu toys are made from polyurethane resin, a durable and lightweight material that allows for intricate detailing. The production process involves molding, painting, and assembly, with each character receiving hand-finished touches to ensure consistency and quality. Limited-edition releases and artist collaborations often feature unique colorways or additional accessories.

The toys are distributed through the official Labubu website, select retailers, and pop-up events. Superplastic also offers subscription-based services, such as the "Labubu Club," which provides members with exclusive toys, early access to products, and other perks.

### Digital Integration

Labubu's digital presence is a key component of its success. The characters are featured in animated series, interactive apps, and social media content, creating a multi-dimensional experience for fans. The brand leverages platforms like YouTube, Instagram, and TikTok to engage with its audience, often incorporating user-generated content and fan art into its campaigns.

In addition to animations, Labubu has explored augmented reality (AR) experiences and virtual events, further blurring the line between physical and digital collectibles.

## Important Facts

<FactList>
<ul>
<li><strong>Founded in 2018:</strong> Labubu was launched by Superplastic, a New York-based entertainment studio.</li>
<li><strong>Polyurethane Resin Toys:</strong> The collectibles are made from durable, hand-painted polyurethane resin.</li>
<li><strong>Digital-First Approach:</strong> Labubu characters are primarily introduced through animated shorts and online content before being released as physical toys.</li>
<li><strong>Collaborations:</strong> The brand has partnered with artists, musicians, and brands to create limited-edition releases.</li>
<li><strong>Community-Driven:</strong> Labubu encourages fan engagement through social media, fan art, and interactive experiences.</li>
<li><strong>Global Reach:</strong> The toys and digital content are distributed worldwide, with a strong presence in North America, Europe, and Asia.</li>
</ul>
</FactList>

## Timeline

<Timeline items={[
  { date: "2018", title: "Superplastic launches Labubu w", description: "Superplastic launches Labubu with the release of animated shorts featuring characters like Janky and Guggimon." },
  { date: "2019", title: "First physical Labubu toys", description: "First physical Labubu toys are introduced, debuting at art and toy conventions." },
  { date: "2020", title: "Labubu expands its digital", description: "Labubu expands its digital presence with a YouTube channel and social media campaigns." },
  { date: "2021", title: "Limited-edition collaborations", description: "Limited-edition collaborations with artists and brands are announced, including partnerships with streetwear labels." },
  { date: "2022", title: "Labubu Club, a subscription", description: "Labubu Club, a subscription service, is launched, offering exclusive toys and early access to products." },
  { date: "2023", title: "The brand introduces augmented", description: "The brand introduces augmented reality (AR) experiences and virtual events to enhance fan engagement." }
]} />

## Related Terms

<RelatedTerms>
<ul>
<li><strong>Art toy</strong> – A collectible item that combines artistic design with toy culture, often produced in limited editions.</li>
<li><strong>Collectible</strong> – An item valued and sought after by enthusiasts for its rarity, uniqueness, or cultural significance.</li>
<li><strong>Superplastic</strong> – The entertainment studio behind Labubu, known for its digital and physical media productions.</li>
<li><strong>Street art</strong> – A form of visual art that is created in public spaces, often influencing the aesthetic of Labubu characters.</li>
<li><strong>Polyurethane resin</strong> – A synthetic material used in the production of Labubu toys, valued for its durability and detail.</li>
</ul>
</RelatedTerms>

## FAQ

<FAQ>
<details>
<summary>What is Labubu?</summary>
<p>Labubu is a line of collectible art toys and digital characters created by Superplastic. The brand features colorful, playful figurines inspired by street art and pop culture.</p>
</details>
<details>
<summary>Who creates Labubu?</summary>
<p>Labubu is created by Superplastic, an entertainment studio based in New York City.</p>
</details>
<details>
<summary>Where can I buy Labubu toys?</summary>
<p>Labubu toys are available on the official website ([labubuart.com](https://labubuart.com)), select retailers, and at special events.</p>
</details>
<details>
<summary>Are Labubu toys limited edition?</summary>
<p>Yes, many Labubu toys are produced in limited quantities, with some releases featuring exclusive designs or collaborations.</p>
</details>
<details>
<summary>How are Labubu characters developed?</summary>
<p>Each character is designed through a collaborative process involving artists, animators, and writers. Their personalities and backstories are explored through digital animations and online content.</p>
</details>
<details>
<summary>Does Labubu have a digital presence?</summary>
<p>Yes, Labubu characters are featured in animated shorts, interactive apps, and social media content, creating a multi-dimensional experience for fans.</p>
</details>
<details>
<summary>What materials are Labubu toys made from?</summary>
<p>Labubu toys are made from polyurethane resin, a durable and lightweight material that allows for intricate detailing.</p>
</details>
</FAQ>

## References

<ReferenceList>
<ol>
<li>Superplastic. "About Us."</li>
<li>Labubu. "Shop."</li>
<li>Forbes. "How Superplastic Is Blending Art, Toys, And Digital Entertainment."</li>
<li>The New York Times. "The Rise of the Art Toy."</li>
</ol>
</ReferenceList>

